import type { SectionProps } from '../Home';
import React, { useEffect, useRef } from 'react';
import Collapse from '../../../components/transitions/Collapse';
import { MaterialIcon } from '../../../components';
import { Headline, Section } from '../../../global';
import { GLOBAL_ICONS } from '../../../util/vars';
import classnames from 'classnames';
import { MEDIUM_TRANSITION_DURATION, getAnimationDurationAsInt } from '../../../util/styled';

type Props = {
  /**
   * Section title/headline.
   */
  headline: string;

  dataCy: string;
  /**
   * Section content.
   */
  open: SectionProps['open'];

  setOpen: () => void;

  children: React.ReactNode;

  strapline?: React.ReactNode | string;

  hasContainer?: boolean;

  hasDivider?: boolean;

  headerComponent?: React.ReactNode | null;

  shake?: boolean;

  scrollToTop?: boolean;

  /**
   * Scrolls the section into view when it opens.
   */
  autoScroll?: boolean;
};

const default_duration = getAnimationDurationAsInt(MEDIUM_TRANSITION_DURATION);

const CollapseSection = ({ headline, open = false, setOpen, dataCy, strapline, hasContainer = true, hasDivider = true, children, headerComponent = null, shake = false, scrollToTop = false, autoScroll = true }: Props): JSX.Element => {

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (autoScroll && open && ref.current) {
      setTimeout(() => {
        if (ref.current) {
          if (scrollToTop) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            const { top } = ref.current.getBoundingClientRect();
            window.scrollTo({ top: top + window.scrollY - 10, behavior: 'smooth' });
          }
        }
      }, default_duration);
    }
  }, [ open ]);

  return (
    <Section data-cy={dataCy} ref={ref}>
      <div className={`bg-white rounded-t-default sticky xl:static top-0 z-max-1 cursor-pointer ${classnames({ 'border-b': hasDivider, 'rounded-b-default': !open, 'animate-nudgeShake': shake })}`}>
        <div className={'container flex items-start md:items-center justify-between'} onClick={() => setOpen()}>
          <Headline title={headline} strapline={strapline} />
          <div>
            <MaterialIcon
              icon={GLOBAL_ICONS[open ? 'toggle_on' : 'toggle_off']}
            />
          </div>
        </div>
        {headerComponent}
      </div>
      <Collapse when={open}>
        <div className={classnames({ 'container': hasContainer })}>
          {children}
        </div>
      </Collapse>
    </Section>
  );
};

CollapseSection.displayName = 'CollapseSection';

export default CollapseSection;
